import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  Renderer2,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomAttributesService } from '@backbase/ui-ang/services';

/**
 * Available icon size variants.
 *
 * @remarks
 * Size values map to predefined icon dimensions in the Backbase theme.
 * The `md` size is the default when no size is specified.
 *
 * @example
 *
 * ```html
 * <bb-icon-ui name="star" size="sm"></bb-icon-ui>
 * ```
 */
export type IconSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/**
 * Displays an icon with configurable size, color, and appearance options.
 *
 * @remarks
 * Renders scalable icons from the Backbase icon library with support for sizing, colors,
 * animation, RTL flipping, and accessibility.
 *
 * @example
 *
 * ```html
 * <bb-icon-ui name="star" size="md"></bb-icon-ui>
 * ```
 *
 * @example
 *
 * ```html
 * <bb-icon-ui name="visibility" aria-label="Show password"></bb-icon-ui>
 * ```
 *
 * @example
 *
 * ```html
 * <bb-icon-ui name="error" size="sm" color="light"></bb-icon-ui>
 * ```
 */
@Component({
  selector: 'bb-icon-ui',
  templateUrl: './icon.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IconComponent implements OnInit, AfterViewInit {
  private _cropped = false;
  private _flipOnRtl = false;

  /**
   * Reference to the inner icon span. ARIA attributes from the host element are moved
   * onto this element in {@link ngAfterViewInit} so that they are paired with the
   * `role="img"` it carries (the host custom element has no implicit role).
   */
  @ViewChild('iconEl', { static: true }) private readonly iconEl!: ElementRef<HTMLElement>;

  /**
   * The name of the icon from the Backbase icon library.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="star"></bb-icon-ui>
   * ```
   *
   * @example
   *
   * ```html
   * <bb-icon-ui [name]="paginationIcons.first"></bb-icon-ui>
   * ```
   */
  @Input() name: string | undefined;

  /**
   * The size of the icon represented as a size name contained in backbase-theme. Defaults to `md`.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="error" size="sm"></bb-icon-ui>
   * ```
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="star" size="xxl"></bb-icon-ui>
   * ```
   */
  @Input() size: IconSize = 'md';

  /**
   * The color of the icon represented as a color name contained in backbase-theme.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="star" color="light"></bb-icon-ui>
   * ```
   */
  @Input() color: string | undefined;

  /**
   * Whether the icon should be animated. Defaults to `false`.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="cached" size="lg" [animate]="animate"></bb-icon-ui>
   * ```
   */
  @Input() animate = false;

  /**
   * Input that can be used to specify the `aria-label` attribute for accessibility.
   *
   * @remarks
   * When provided as a plain attribute (e.g. `aria-label="foo"`) it is moved from the
   * host element onto the inner `role="img"` span in {@link ngAfterViewInit} to satisfy
   * `aria-prohibited-attr`. When provided via property binding (`[aria-label]="foo"`),
   * the inner span receives it through the template binding below.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="visibility" aria-label="Show password"></bb-icon-ui>
   * ```
   */
  @Input('aria-label') ariaLabel: string | undefined;

  /**
   * Input that can be used to specify the `aria-labelledby` attribute for accessibility.
   *
   * @remarks
   * Behaves like {@link ariaLabel}: plain-attribute values are moved from host to the inner
   * span in {@link ngAfterViewInit}, property-binding values flow through the template binding.
   *
   * @example
   *
   * ```html
   * <span id="my-label">Permitted</span>
   * <bb-icon-ui name="check" aria-labelledby="my-label"></bb-icon-ui>
   * ```
   */
  @Input('aria-labelledby') ariaLabelledby: string | undefined;

  /**
   * Whether the icon should be cropped horizontally.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="success" size="md" color="primary" cropped></bb-icon-ui>
   * ```
   */
  @Input()
  get cropped(): boolean | string {
    return this._cropped;
  }

  /**
   * Sets whether the icon should be cropped horizontally.
   *
   * @param value - Boolean or string value (string "false" treated as false)
   *
   * @example
   *
   * ```typescript
   * iconComponent.cropped = true;
   * ```
   */
  set cropped(value: boolean | string) {
    this._cropped = value != null && `${value}` !== 'false';
  }

  /**
   * Whether the icon should be flipped when the page direction is RTL (right-to-left).
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="arrow-back" size="md" flipOnRtl="true" cropped></bb-icon-ui>
   * ```
   */
  @Input() set flipOnRtl(value: boolean) {
    this._flipOnRtl = value;
  }

  /**
   * Gets whether the icon should be flipped in RTL mode.
   *
   * @returns true if icon flips in RTL mode
   *
   * @example
   *
   * ```typescript
   * if (iconComponent.flipOnRtl) {
   *   // Icon will flip when page direction is RTL
   * }
   * ```
   */
  get flipOnRtl(): boolean {
    return this._flipOnRtl;
  }

  /**
   * Computes CSS class names based on icon configuration.
   *
   * @remarks
   * This getter dynamically generates the list of CSS classes applied to the icon element
   * based on the configured properties like name, size, color, and animation state.
   * The classes are filtered to remove any falsy values before being applied.
   *
   * @returns Array of CSS class names to apply to the icon element
   *
   * @example
   *
   * ```html
   * <span [ngClass]="classNames" class="bb-icon"></span>
   * ```
   */
  get classNames(): string[] {
    return [
      this.name ? `bb-icon-${this.name}` : '',
      this.animate ? 'bb-icon--animate' : '',
      this.flipOnRtl ? 'bb-icon--rtl' : '',
      this.size ? `bb-icon--${this.size}` : 'bb-icon--md',
      this._cropped ? 'bb-icon--cropped' : '',
      this.color ? `bb-icon--${this.color}` : '',
    ].filter(Boolean);
  }

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly domAttributesService: DomAttributesService,
  ) {}

  /**
   * Validates that the required name input is provided.
   *
   * @remarks
   * Throws an error if the name input is not provided, as the icon cannot render without a name.
   * This ensures early detection of configuration errors.
   */
  ngOnInit() {
    if (typeof this.name === 'undefined') {
      throw new Error(`"name" input is required in "${this.constructor.name}"`);
    }
  }

  /**
   * Moves any `aria-*` attributes from the host element onto the inner `role="img"` span.
   *
   * @remarks
   * The host `<bb-icon-ui>` is a custom element with no implicit role, so `aria-label` /
   * `aria-labelledby` etc. on it are flagged by axe as `aria-prohibited-attr` (WCAG 4.1.2).
   * The inner span already carries `role="img"`, so relocating the attributes there keeps
   * the accessible name on a properly-roled element.
   */
  ngAfterViewInit() {
    if (this.iconEl?.nativeElement) {
      this.domAttributesService.moveAriaAttributes(
        this.elementRef.nativeElement,
        this.iconEl.nativeElement,
        this.renderer,
      );
    }
  }
}
